import React, { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import useCredentials, {
  ECredentialsType,
  ICredential
} from 'api/useCredentials';
import { getApiErrorMessage } from 'utils/errorsTextHelper';
import { EToasterStatus, showToast } from 'utils/showToast';

import { Separator } from 'components/ui/separator';

import { CredentialsRow } from './CredentialsRow';
import {
  ECredentialValueRef,
  InvalidationCredentialModal
} from './InvalidationCredentialModal';
import { SettingsCard } from './SettingsCard';

interface ICredentialsSectionV2Props {
  onCopy: (value: string, label: string) => void;
}

interface ICredentialInvalidationRequest {
  valueRef: ECredentialValueRef;
  credential: ICredential;
  credentialType: ECredentialsType;
  label: string;
}

const CREDENTIALS_TYPES = [
  {
    label: 'Main Key',
    valueRef: ECredentialValueRef.KEY,
    description: 'Secret key used to authenticate server API requests.',
    type: ECredentialsType.MAIN_KEY
  },
  {
    label: 'Checkout Public Key',
    valueRef: ECredentialValueRef.KEY,
    description: 'Public key used to authenticate checkout requests.',
    type: ECredentialsType.CHECKOUT_PUBLIC_KEY
  },
  {
    label: 'Publisher Token',
    valueRef: ECredentialValueRef.TOKEN,
    description: 'Token used to authenticate publisher requests.',
    type: ECredentialsType.PUBLISHER_TOKEN
  }
];

export const CredentialsSectionV2: React.FC<ICredentialsSectionV2Props> = ({
  onCopy
}) => {
  const { publisherId } = useParams();
  const { getCredentials, rotateCredential, revokeCredential } =
    useCredentials(publisherId);
  const [credentialInvalidationRequest, setCredentialInvalidationRequest] =
    useState<ICredentialInvalidationRequest | null>(null);
  const credentialsByType = useMemo(
    () =>
      (getCredentials.data || []).reduce<
        Partial<Record<ECredentialsType, ICredential[]>>
      >((credentials, credential) => {
        credentials[credential.type] = [
          ...(credentials[credential.type] || []),
          credential
        ];
        return credentials;
      }, {}),
    [getCredentials.data]
  );

  const handleCopyCredential = useCallback(
    (value: string, label: string): void => {
      onCopy(value, label);
    },
    [onCopy]
  );

  const handleRotateCredential = useCallback(
    (credentialType: ECredentialsType): void => {
      rotateCredential.mutate(credentialType, {
        onSuccess: () => {
          getCredentials.refetch();
          showToast({
            message: 'Credential created successfully',
            status: EToasterStatus.SUCCESS
          });
        },
        onError: (error) => {
          showToast({
            message: 'Failed to rotate credential',
            status: EToasterStatus.ERROR,
            description: getApiErrorMessage(error)
          });
        }
      });
    },
    [getCredentials, rotateCredential]
  );

  const handleRequestRevokeCredential = useCallback(
    (
      credential: ICredential,
      credentialType: ECredentialsType,
      label: string,
      valueRef: ECredentialValueRef
    ): void => {
      setCredentialInvalidationRequest({
        credential,
        credentialType,
        label,
        valueRef
      });
    },
    []
  );

  const handleCancelInvalidation = useCallback((): void => {
    setCredentialInvalidationRequest(null);
  }, []);

  const handleApproveInvalidation = useCallback((): void => {
    if (!credentialInvalidationRequest) return;
    revokeCredential.mutate(
      {
        credentialId: credentialInvalidationRequest.credential.id,
        credentialType: credentialInvalidationRequest.credentialType
      },
      {
        onSuccess: () => {
          setCredentialInvalidationRequest(null);
          showToast({
            message: 'Credential invalidated successfully',
            status: EToasterStatus.SUCCESS
          });
        },
        onError: (error) => {
          showToast({
            message: 'Failed to invalidate credential',
            status: EToasterStatus.ERROR,
            description: getApiErrorMessage(error)
          });
        }
      }
    );
  }, [credentialInvalidationRequest, revokeCredential]);

  const renderCredentials = (): React.ReactNode => {
    if (getCredentials.isLoading || !getCredentials.data) {
      return null;
    }

    return CREDENTIALS_TYPES.map((credentialRow, index) => {
      const credentials = credentialsByType[credentialRow.type];
      if (!credentials?.length) return null;

      return (
        <React.Fragment key={credentialRow.type}>
          {index > 0 && <Separator className="mx-4 w-auto bg-base-border" />}
          <CredentialsRow
            valueRef={credentialRow.valueRef}
            label={credentialRow.label}
            description={credentialRow.description}
            credentialType={credentialRow.type}
            credentials={credentials}
            isRotating={rotateCredential.isLoading}
            onCopyCredential={handleCopyCredential}
            onRotateCredential={handleRotateCredential}
            onRevokeCredential={handleRequestRevokeCredential}
          />
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <SettingsCard
        title="Credentials"
        description="Keys and tokens required to authenticate your API and SDK requests."
        hasHeaderSeparator
      >
        <div className="flex flex-col gap-6">{renderCredentials()}</div>
      </SettingsCard>
      {credentialInvalidationRequest && (
        <InvalidationCredentialModal
          credential={credentialInvalidationRequest.credential}
          valueRef={credentialInvalidationRequest.valueRef}
          label={credentialInvalidationRequest.label}
          isApproving={revokeCredential.isLoading}
          onCancel={handleCancelInvalidation}
          onApprove={handleApproveInvalidation}
        />
      )}
    </>
  );
};
